/**
 * Scrolls a Kanban card into view (AVA-755), without the library's own `scroll` action.
 *
 * Why not `board.scroll({ to: 'card' })`: on this board's layout (scrollType 'column' + renderType 'lazy',
 * `column:lazy`) DHTMLX Kanban 1.5.12 handles it in VirtualScrollColumn by writing to a store it cannot
 * write (`TypeError: e.set is not a function`). The error aborts the library's render pass: the column's
 * virtual list stops drawing, so a card that just moved into it never appears and the column shows an empty
 * area when scrolled (seen live 2026-10-20 on activity 11320; reproduced headless with the real library).
 *
 * A lazy column draws only the cards near its scroll position, so a card off screen has no element yet:
 * the column's own list is scrolled to the card's place first, then, once the card is drawn, it is brought
 * fully into view. The list redraws on its scroll event, and re-measures itself asynchronously after its
 * cards change; a scroll that lands during that re-measure is lost (the list keeps drawing its old window,
 * which shows as an empty area). So the column settles first, and the scroll event is sent again while the
 * card is not drawn yet.
 *
 * No imports, so `npm run test:unit` (node --test) can load it directly.
 */

/** The library's attribute on a card's element (its value is the card id). */
const CARD_ATTRIBUTE = 'data-drag-item';
/** The scrolling list of one column in the `column:lazy` layout (a collapsed column has none). */
const COLUMN_LIST_SELECTOR = '.wx-virtual-list';
/** Frames to wait for a smooth scroll to reach the card (a few hundred ms) before jumping there instead. */
export const SMOOTH_FRAMES = 45;
/** Frames to wait, in all, for the column to draw the card. */
export const MAX_DRAW_FRAMES = 90;
/** Frames a column gets to re-measure after its cards changed, before it is scrolled. */
export const SETTLE_FRAMES = 2;
/** While the card is not drawn, the list's scroll event is sent again every this many frames. */
export const REDRAW_EVERY = 10;
const SCROLL_EVENT = 'scroll';

const nextFrame = () => new Promise((resolve) => requestAnimationFrame(() => resolve()));
const escapeId = (id) => (typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : String(id).replace(/["\\]/g, '\\$&'));

/**
 * @param {Element} root the board's element (`#root`)
 * @param {string} id the card
 * @returns {Element|null} the card's element, when the column has drawn it
 */
export function cardElement(root, id) {
  return root.querySelector(`[${CARD_ATTRIBUTE}="${escapeId(String(id))}"]`);
}

/**
 * The card is drawn and fully inside both its column's list and the board (a lazy list also draws a few rows
 * just outside its visible area, so being drawn is not being on screen).
 * @param {Element} root the board's element
 * @param {string} id the card
 */
export function cardInView(root, id) {
  const element = cardElement(root, id);
  if (!element) return false;
  const box = element.getBoundingClientRect();
  return [root, element.closest(COLUMN_LIST_SELECTOR)]
    .filter(Boolean)
    .map((frame) => frame.getBoundingClientRect())
    .every((frame) => box.top >= frame.top && box.bottom <= frame.bottom && box.left >= frame.left && box.right <= frame.right);
}

/**
 * Where a card sits in the board's state: the cards of its column, in order, and its position among them.
 * @param {{ cardsMap: object, columnKey: string, rowKey?: string }} state board.api.getState()
 * @param {object} card board.getCard(id)
 * @returns {{ cards: object[], index: number, count: number }|null}
 */
export function cardPlace(state, card) {
  if (!state || !card) return null;
  const columnId = card[state.columnKey];
  const cards = (state.rowKey ? state.cardsMap[`${columnId}:${card[state.rowKey]}`] : null) || state.cardsMap[columnId] || [];
  const index = cards.findIndex((c) => String(c.id) === String(card.id));
  return index < 0 ? null : { cards, index, count: cards.length };
}

/**
 * The list of the card's column, found from a card of that column the list has drawn. Not by the column's
 * position: a collapsed column has no list, so the lists of the columns after it would be one off.
 * @param {Element} root the board's element
 * @param {object[]} cards the column's cards (cardPlace)
 * @returns {Element|null} null when the column draws no card (it is collapsed)
 */
export function columnList(root, cards) {
  for (const card of cards) {
    const list = cardElement(root, card.id)?.closest(COLUMN_LIST_SELECTOR);
    if (list) return list;
  }
  return null;
}

/**
 * The scroll position that puts the card's estimated place in the middle of its column's list.
 * @param {{ index: number, count: number }} place
 * @param {{ scrollHeight: number, clientHeight: number }} list
 */
export function estimatedTop(place, list) {
  const top = (place.index / Math.max(place.count, 1)) * list.scrollHeight - list.clientHeight / 2;
  return Math.max(0, Math.min(top, list.scrollHeight - list.clientHeight));
}

/**
 * @param {{ root: Element, board: object, id: string, behavior: 'smooth'|'auto', frame?: () => Promise<void> }} input
 * @returns {Promise<boolean>} true when the card was brought into view
 */
export async function scrollCardIntoView({ root, board, id, behavior, frame = nextFrame }) {
  for (let i = 0; i < SETTLE_FRAMES; i += 1) await frame();
  let element = cardElement(root, id);
  if (!element) {
    const place = cardPlace(board.api.getState(), board.getCard(id));
    const list = place && columnList(root, place.cards);
    if (!list) return false;
    const top = estimatedTop(place, list);
    list.scrollTo({ top, behavior });
    for (let i = 0; i < MAX_DRAW_FRAMES && !element; i += 1) {
      // A smooth scroll that has not brought the card in by now (or a browser that does not animate it) jumps.
      if (i === SMOOTH_FRAMES && behavior !== 'auto') list.scrollTo({ top, behavior: 'auto' });
      await frame();
      element = cardElement(root, id);
      if (!element && i % REDRAW_EVERY === REDRAW_EVERY - 1) list.dispatchEvent(new Event(SCROLL_EVENT));
    }
  }
  if (!element) return false;
  element.scrollIntoView({ behavior, block: 'nearest', inline: 'nearest' });
  return true;
}
